import { useCallback, useEffect, useRef, useState } from 'react';

import {
  SpreadsheetSelectionCell,
  SpreadsheetSelectionHeaderCellType,
} from './types';
import {
  checkColumnSelection,
  checkRowSelection,
  generateCopyPasteText,
  getCellsInRectangularRange,
  getRangeData,
  isSameCell,
  translateCell,
} from './utils';

export interface UseSpreadsheetSelectionProps {
  numberOfRows: number;
  numberOfColumns: number;
  onAttemptCopy: (content: string) => void;
  startColumn?: number;
  // Optional controlled state
  selectedCell?: SpreadsheetSelectionCell | null;
  onSetSelectedCell?: (cell: SpreadsheetSelectionCell | null) => void;
  extentCell?: SpreadsheetSelectionCell | null;
  onSetExtentCell?: (cell: SpreadsheetSelectionCell | null) => void;
}
/**
 * A utility hook to deliver headless single cell, range, drag, and arrow-key based selection for a 2D grid.  Plug and play into any table solution, such as HTML table or CSS grid.
 * Takes optional state management for selected cell and extent cell.
 *
 * NOTE: For multi-level data, all cell interaction functions will use an inputted visibleRowNumbers input, so the
 *  numberOfRows input will not be impactful for multi-level data
 * @returns
 */
export const useSpreadsheetSelection = ({
  numberOfRows,
  numberOfColumns,
  onAttemptCopy,
  startColumn = 0,
  // Optional controlled state
  selectedCell: externalSelectedCell = null,
  onSetSelectedCell,
  extentCell: externalExtentCell = null,
  onSetExtentCell,
}: UseSpreadsheetSelectionProps) => {
  // Internal state fallback if no external states provided
  const [internalSelectedCell, setInternalSelectedCell] =
    useState<SpreadsheetSelectionCell | null>(null);
  const [internalExtentCell, setInternalExtentCell] =
    useState<SpreadsheetSelectionCell | null>(null);

  // Track the selected range of cells
  const [rangeSelectedCells, setRangeSelectedCells] = useState<
    SpreadsheetSelectionCell[]
  >([]);

  // Use external state if provided, otherwise use internal state
  const selectedCell = externalSelectedCell ?? internalSelectedCell;
  const setSelectedCellState = onSetSelectedCell ?? setInternalSelectedCell;
  const extentCell = externalExtentCell ?? internalExtentCell;
  const setExtentCellState = onSetExtentCell ?? setInternalExtentCell;

  // Refs to mirror states
  const selectedCellRef = useRef(selectedCell);
  const extentCellRef = useRef(extentCell);
  const rangeSelectedCellsRef = useRef(rangeSelectedCells);

  useEffect(() => {
    selectedCellRef.current = selectedCell;
  }, [selectedCell]);
  useEffect(() => {
    rangeSelectedCellsRef.current = rangeSelectedCells;
  }, [rangeSelectedCells]);
  useEffect(() => {
    extentCellRef.current = extentCell;
  }, [extentCell]);

  const setSelectedAndExtentCell = useCallback(
    (
      selectedCellState: SpreadsheetSelectionCell | null,
      extentCellState: SpreadsheetSelectionCell | null,
    ) => {
      setSelectedCellState(selectedCellState);
      setExtentCellState(extentCellState);
      if (selectedCellState && extentCellState) {
        setRangeSelectedCells(
          getCellsInRectangularRange(selectedCellState, extentCellState),
        );
      } else {
        setRangeSelectedCells([]);
      }
    },
    [setSelectedCellState, setExtentCellState],
  );

  const setSelectedCell = useCallback(
    (selectedCellState: SpreadsheetSelectionCell | null) => {
      if (
        !isSameCell(selectedCellState, selectedCellRef.current) ||
        extentCellRef.current != null
      ) {
        setSelectedCellState(selectedCellState);
        setRangeSelectedCells([]);
        setExtentCellState(null);
      }
    },
    [setSelectedCellState, setExtentCellState],
  );

  const setExtentCell = useCallback(
    (extentCellState: SpreadsheetSelectionCell | null) => {
      if (selectedCellRef.current == null) {
        setSelectedCell(extentCellState);
      } else {
        if (extentCellState != null) {
          setRangeSelectedCells(
            getCellsInRectangularRange(
              selectedCellRef.current as SpreadsheetSelectionCell,
              extentCellState,
            ),
          );
        } else {
          setRangeSelectedCells([]);
        }
        setExtentCellState(extentCellState);
      }
    },
    [setSelectedCell, setExtentCellState],
  );

  /**
   * Handler for keyboard events on the grid, including arrow keys (with and without shift), copy, paste, and select all.
   * @param event
   */
  const gridOnKeydown = useCallback(
    (
      event: React.KeyboardEvent<HTMLDivElement>,
      getContentOfCell: (cell: SpreadsheetSelectionCell) => string,
      visibleRowNumbers?: number[],
    ) => {
      if (selectedCellRef.current != null) {
        let translateParams: SpreadsheetSelectionCell | null = null;
        switch (event.key) {
          // Arrow key navigation
          case 'ArrowLeft':
            event.preventDefault();
            translateParams = { row: 0, column: -1 };
            break;
          case 'ArrowRight':
            event.preventDefault();
            translateParams = { row: 0, column: 1 };
            break;
          case 'ArrowDown':
            event.preventDefault();
            translateParams = { row: 1, column: 0 };
            break;
          case 'ArrowUp':
            event.preventDefault();
            translateParams = { row: -1, column: 0 };
            break;
          // Tab navigation
          case 'Tab':
            event.preventDefault();
            translateParams = {
              row: 0,
              column: 1 * (event.shiftKey ? -1 : 1),
            };
            break;
          // Ctrl-C — Copy
          case 'c':
          case 'C':
            if (event.metaKey || event.ctrlKey) {
              event.preventDefault();
              // If we have more than one cell selected, copy it as formatted information
              if (rangeSelectedCellsRef.current.length) {
                onAttemptCopy(
                  generateCopyPasteText(
                    rangeSelectedCellsRef.current,
                    getContentOfCell,
                  ),
                );
                // Otherwise, copy the contents of the single selected cell
              } else {
                const s = getContentOfCell(selectedCellRef.current);
                onAttemptCopy(s !== '' ? s : ' ');
              }
            }
            break;
          // Ctrl-A — Select All
          case 'a':
          case 'A':
            if (event.metaKey || event.ctrlKey) {
              event.preventDefault();
              setSelectedAndExtentCell(
                { row: 0, column: startColumn },
                {
                  row: visibleRowNumbers
                    ? Math.max(...visibleRowNumbers)
                    : numberOfRows - 1,
                  column: numberOfColumns - 1,
                },
              );
            }
            break;
          default:
        }

        // Translate extent or selected cell if applicable.
        if (translateParams != null) {
          // If default press, move the selected cell.
          if (!event.shiftKey || event.key === 'Tab') {
            setSelectedCell(
              translateCell(
                selectedCellRef.current,
                translateParams,
                numberOfRows,
                numberOfColumns,
                visibleRowNumbers,
                startColumn,
              ),
            );
          }
          // If shift press, move the extent.
          else {
            setExtentCell(
              translateCell(
                extentCellRef.current || selectedCellRef.current,
                translateParams,
                numberOfRows,
                numberOfColumns,
                visibleRowNumbers,
                startColumn,
              ),
            );
          }
        }
      }
    },
    [
      numberOfColumns,
      startColumn,
      numberOfRows,
      onAttemptCopy,
      setExtentCell,
      setSelectedAndExtentCell,
      setSelectedCell,
    ],
  );

  // BODY CELL STUFF ------------------------------------------------------------------------ //
  const bodyCellSelected = useCallback(
    (cell: SpreadsheetSelectionCell) => isSameCell(cell, selectedCell),
    [selectedCell],
  );
  const bodyCellRangeData = useCallback(
    (cell: SpreadsheetSelectionCell) => getRangeData(cell, rangeSelectedCells),
    [rangeSelectedCells],
  );
  const bodyCellOnInteract = useCallback(
    (cell: SpreadsheetSelectionCell, isShiftPressed?: boolean) => {
      if (!isShiftPressed) {
        setSelectedCell(cell);
      } else {
        setExtentCell(cell);
      }
    },
    [setExtentCell, setSelectedCell],
  );

  const bodyCellOnMouseEnter = useCallback(
    (cell: SpreadsheetSelectionCell, event: React.MouseEvent<HTMLElement>) => {
      if (selectedCellRef.current != null && event.buttons === 1) {
        setExtentCell(cell);
      }
    },
    [setExtentCell],
  );
  // ---------------------------------------------------------------------------------------- //
  // HEADER CELLS --------------------------------------------------------------------------- //
  /**
   * Determines if none, some, or all cells in a row or column all selected
   */
  const getRowOrHeaderCellState = useCallback(
    (
      type: SpreadsheetSelectionHeaderCellType,
      index: number,
      visibleRowNumbers?: number[],
    ) => {
      return type === SpreadsheetSelectionHeaderCellType.ROW
        ? checkRowSelection(
            index,
            selectedCell,
            rangeSelectedCells,
            numberOfColumns,
          )
        : checkColumnSelection(
            index,
            selectedCell,
            rangeSelectedCells,
            numberOfRows,
            visibleRowNumbers,
          );
    },
    [numberOfColumns, numberOfRows, rangeSelectedCells, selectedCell],
  );

  const headerCellOnClick = useCallback(
    (
      type: SpreadsheetSelectionHeaderCellType,
      index: number,
      event: React.MouseEvent<HTMLElement>,
      visibleRowNumbers?: number[],
    ) => {
      if (type === SpreadsheetSelectionHeaderCellType.ROW) {
        if (!event.shiftKey) {
          setSelectedAndExtentCell(
            { row: index, column: startColumn },
            { row: index, column: numberOfColumns - 1 },
          );
        } else {
          setExtentCell({ row: index, column: numberOfColumns - 1 });
        }
      } else {
        if (!event.shiftKey) {
          setSelectedAndExtentCell(
            { row: 0, column: index },
            {
              row: visibleRowNumbers
                ? Math.max(...visibleRowNumbers)
                : numberOfRows - 1,
              column: index,
            },
          );
        } else {
          setExtentCell({
            row: visibleRowNumbers
              ? Math.max(...visibleRowNumbers)
              : numberOfRows - 1,
            column: index,
          });
        }
      }
    },
    [
      numberOfColumns,
      startColumn,
      numberOfRows,
      setExtentCell,
      setSelectedAndExtentCell,
    ],
  );
  // ---------------------------------------------------------------------------------------- //

  return {
    // OUTPUTS
    gridOnKeydown,
    bodyCellSelected,
    bodyCellRangeData,
    bodyCellOnInteract,
    bodyCellOnMouseEnter,
    getRowOrHeaderCellState,
    headerCellOnClick,
    // STATE
    selectedCell,
    setSelectedCell,
    rangeSelectedCells,
    setRangeSelectedCells,
    extentCell,
    setExtentCell,
  };
};

export type UseSpreadsheetSelectionReturns = ReturnType<
  typeof useSpreadsheetSelection
>;
